import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Checkbox, Chip, CircularProgress, FormControlLabel, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import SearchableSelect from '../common/SearchableSelect';
import { SalesOrderService, fetchAllPages } from '../../services/salesOrder.service';
import { CustomerService } from '../../services/customer.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { Store } from '../../interfaces/store.interface';
import { getStoresByInstitutionId } from '../../services/store.service';
import SalesItemSearchSelect from './SalesItemSearchSelect';
import { BackOrderRow } from '../../interfaces/sales/salesOrder.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { formatDate } from '../../utils/dateUtils';

const BackOrders: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution();
  const [rows, setRows] = useState<BackOrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [stores, setStores] = useState<Store[]>([]);
  const [storeId, setStoreId] = useState('');
  const [itemId, setItemId] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);

  useEffect(() => {
    fetchAllPages<Customer>((p, n) => CustomerService.listByInstitution(institutionId, p, n, 'ALL')).then(setCustomers).catch(() => setCustomers([]));
    fetchAllPages<Store>((p, n) => getStoresByInstitutionId(p, n, 'ALL')).then(setStores).catch(() => setStores([]));
  }, [institutionId]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await SalesOrderService.backOrders(institutionId, { customerId: customerId || undefined, itemId: itemId || undefined, storeId: storeId || undefined, overdueOnly }));
    } catch (e) {
      toast.showApiError(e, 'Failed to load back-orders');
    } finally {
      setLoading(false);
    }
  }, [institutionId, customerId, itemId, storeId, overdueOnly]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.backOrders.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Back-orders</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="sales.backOrders.filters">
        <SearchableSelect data-help="sales.backOrders.customer" label="Customer" size="small" sx={{ flex: '1 1 240px', maxWidth: 340 }} options={customers}
          value={customerId || null} onChange={id => setCustomerId(id)} getOptionLabel={c => c.name} clearable />
        <SalesItemSearchSelect data-help="sales.backOrders.item" label="Item" value={itemId || null} onChange={id => setItemId(id)} sx={{ flex: '1 1 240px', maxWidth: 340 }} />
        <SearchableSelect data-help="sales.backOrders.store" label="Store" size="small" sx={{ flex: '1 1 240px', maxWidth: 340 }} options={stores}
          value={storeId || null} onChange={id => setStoreId(id)} getOptionLabel={s => s.storeName} clearable />
        <FormControlLabel data-help="sales.backOrders.overdueOnly" control={<Checkbox checked={overdueOnly} onChange={e => setOverdueOnly(e.target.checked)} />} label="Overdue only" />
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} data-help-section="sales.backOrders.table">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="sales.backOrders.col.order">Order</TableCell><TableCell data-help="sales.backOrders.col.customer">Customer</TableCell><TableCell data-help="sales.backOrders.col.item">Item</TableCell>
                <TableCell align="right" data-help="sales.backOrders.col.ordered">Ordered</TableCell><TableCell align="right" data-help="sales.backOrders.col.delivered">Delivered</TableCell>
                <TableCell align="right" data-help="sales.backOrders.col.backOrder">Back-order</TableCell><TableCell align="right" data-help="sales.backOrders.col.inStock">In stock</TableCell>
                <TableCell data-help="sales.backOrders.col.requested">Requested</TableCell><TableCell align="right" data-help="sales.backOrders.col.age">Age (days)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.orderLineId} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/sales/orders/${r.orderId}`)}>
                  <TableCell>{r.orderNo}</TableCell><TableCell>{r.customerName}</TableCell>
                  <TableCell>{r.itemCode ? `${r.itemCode} - ${r.itemName}` : r.description}</TableCell>
                  <TableCell align="right">{r.orderedQty}</TableCell><TableCell align="right">{r.deliveredQty}</TableCell>
                  <TableCell align="right">{r.backOrderQty}</TableCell>
                  <TableCell align="right">{r.availableQty ?? '-'}</TableCell>
                  <TableCell>
                    {r.requestedDeliveryDate ? formatDate(r.requestedDeliveryDate) : '-'}
                    {r.overdue && <Chip label="Overdue" color="error" size="small" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell align="right">{r.ageDays}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={9}>Nothing is waiting to be delivered.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default BackOrders;
